// M004-F0103 / M005-F0017 / WEB-TERM-019
//
// The organisation list is a display and request-selection boundary, not an
// authorisation boundary.  Core remains authoritative, but the browser must
// not turn a linked non-admin membership into a selectable admin scope.

const stringId = (value) => (value === undefined || value === null ? '' : String(value));

const organisationId = (organisation = {}) => stringId(
  organisation.id ?? organisation.organisation_id ?? organisation.organisationId,
);

const membershipRole = (organisation = {}) => String(
  organisation.user_role
  ?? organisation.userRole
  ?? organisation.membership_role
  ?? organisation.membershipRole
  ?? '',
).trim().toLowerCase();

export const isGlobalSuperAdmin = (user) => user?.role === 'super_admin';

// `viewer` (M004-F0764): a caller admitted by the room_devices_read CAPABILITY, not by an
// admin membership, sees every organisation they belong to, read-only. Core still decides
// which rooms each one returns (organisation capability rooms_view).
export const selectAdminRoomsOrganisations = (organisations, user, { viewer = false } = {}) => {
  const seen = new Set();
  const validOrganisations = (Array.isArray(organisations) ? organisations : []).filter((organisation) => {
    const id = organisationId(organisation);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  if (isGlobalSuperAdmin(user) || viewer) return validOrganisations;

  // /organisations/mine supplies user_role from the current membership.  Do
  // not fall back to an organisation's own fields or the user's global role:
  // either could accidentally turn a non-admin membership into admin scope.
  return validOrganisations.filter((organisation) => membershipRole(organisation) === 'admin');
};

export const resolveAdminRoomsSelection = (selection, organisations, user, { viewer = false } = {}) => {
  const selectedId = stringId(selection);
  const allowedOrganisations = selectAdminRoomsOrganisations(organisations, user, { viewer });

  // The empty value is the explicit All Organisations choice, and is valid
  // only for a current global super_admin.
  if ((isGlobalSuperAdmin(user) || viewer) && !selectedId) return '';

  if (allowedOrganisations.some((organisation) => organisationId(organisation) === selectedId)) {
    return selectedId;
  }

  // A stale or forged selection never reaches a child request.  If a current
  // administered membership exists, recover to that explicit first choice;
  // otherwise remain empty and fail closed.
  return organisationId(allowedOrganisations[0]);
};

export const canLoadAdminRoomsScope = (scopeReady, selection, user, { viewer = false } = {}) => (
  Boolean(scopeReady) && (isGlobalSuperAdmin(user) || viewer || Boolean(stringId(selection)))
);
